import React from 'react';
import styled from 'styled-components';
import { noop } from 'lodash-es';
import { FjCheckbox } from 'components/primitives';

// A per-row checkbox, driven entirely by props. Used by the super-admin tables, which select
// rows one at a time.
//
// It carried a select-all branch too - `isHeader`, plus `data`, `selectAllRows` and
// `deselectAllRows` - but no caller ever passed any of them, so it was dead. Worse, it read
// `data`, which a v8 table supplies post-pagination: wiring a header checkbox to it would have
// silently selected only the visible page. cloud-admin's own checkbox
// (selected-tab-content/checkbox) is the one that does select-all, and it asks which "all" is
// meant by comparing `data` against `allData`. Anything growing a select-all here should reuse
// that rather than reviving this.

interface StyledCheckboxProps {
  disabled?: boolean;
}

const StyledCheckbox = styled(FjCheckbox).attrs<StyledCheckboxProps>(({ theme }) => ({
  color: theme.colors.selectableLightBg,
}))<StyledCheckboxProps>`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

interface TableCheckboxProps {
  rowId: string;
  selectedRows?: Record<string, boolean>;
  selectRow?: (rowId: string) => void;
  deselectRow?: (rowId: string) => void;
  dataTestId?: string;
  disabled?: boolean;
}

class TableCheckbox extends React.Component<TableCheckboxProps> {
  static defaultProps: Partial<TableCheckboxProps> = {
    selectedRows: {},
    selectRow: noop,
    deselectRow: noop,
    dataTestId: undefined,
    disabled: false,
  };

  onSelect = (): void => {
    const { rowId, selectedRows = {}, selectRow = noop, deselectRow = noop } = this.props;

    if (!selectedRows[rowId]) {
      selectRow(rowId);
    } else {
      deselectRow(rowId);
    }
  };

  render(): React.ReactNode {
    const { selectedRows = {}, rowId, dataTestId, disabled } = this.props;
    const checked = !!selectedRows[rowId];

    return (
      <StyledCheckbox
        onChange={this.onSelect}
        checked={checked}
        data-testid={dataTestId}
        disabled={disabled}
      />
    );
  }
}

export default TableCheckbox;
